import { inject, Injectable } from "@angular/core"
import {
  addDoc,
  collection,
  collectionData,
  collectionSnapshots,
  deleteDoc,
  doc,
  docData,
  documentId,
  DocumentReference,
  DocumentSnapshot,
  FieldPath,
  Firestore,
  getDoc,
  increment,
  limit,
  onSnapshot,
  orderBy,
  OrderByDirection,
  query,
  QueryConstraint,
  QueryDocumentSnapshot,
  serverTimestamp,
  sortedChanges,
  Timestamp,
  updateDoc,
  where,
  WhereFilterOp
} from "@angular/fire/firestore"
import { combineLatest, map, Observable, of } from "rxjs"

type HasId = { id: string }
type WhereArray = [string | FieldPath, WhereFilterOp, unknown]

@Injectable({
  providedIn: "root"
})
export class DbService {
  _firestore: Firestore = inject(Firestore)

  AUCTION_COLLECTION = "auctions"
  AUCTIONV2_COLLECTION = "v2auctions"
  AUTO_AUCTION_COLLECTION = "autoauctions"
  EVENT_COLLECTION = "events"
  LEADERBOARD_COLLECTION = "leaderboards"
  NOTIFICATION_COLLECTION = "notifications"
  USERS_COLLECTION = "users"

  documentId = documentId()

  timestamp = serverTimestamp as () => Timestamp

  col(collectionName: string) {
    return collection(this._firestore, collectionName)
  }

  ref(collectionName: string, ident: string) {
    return doc(this.col(collectionName), ident)
  }

  _getQuery(
    collectionName: string,
    wheres?: (WhereArray | undefined)[],
    sortBy?: string,
    topLimit?: number,
    orderByDir: OrderByDirection = "desc"
  ) {
    const constraints: QueryConstraint[] = []
    wheres?.forEach((_where) => {
      if (_where) constraints.push(where(..._where))
    })
    if (sortBy) {
      constraints.push(orderBy(sortBy, orderByDir))
    }
    if (topLimit) {
      constraints.push(limit(topLimit))
    }
    return query(this.col(collectionName), ...constraints)
  }

  public getItems<T>(
    collectionName: string,
    orderByKey?: string,
    orderByDir: OrderByDirection = "desc",
    ...wheres: WhereArray[]
  ) {
    return collectionData(
      this._getQuery(collectionName, wheres, orderByKey, undefined, orderByDir),
      { idField: "id" }
    ) as Observable<T[]>
  }

  public getItemSnapshots<T>(collectionName: string, ...wheres: WhereArray[]) {
    return collectionSnapshots(
      this._getQuery(collectionName, wheres)
    ) as Observable<QueryDocumentSnapshot<T>[]>
  }

  public getTopItem<T>(
    collectionName: string,
    sortBy: string,
    topLimit = 1,
    _where?: WhereArray
  ) {
    return collectionData(
      this._getQuery(collectionName, [_where], sortBy, topLimit),
      { idField: "id" }
    ) as Observable<T[]>
  }

  public getTopItemSnapshot<T>(
    collectionName: string,
    sortBy: string,
    topLimit = 1,
    _where?: WhereArray
  ) {
    return collectionSnapshots(
      this._getQuery(collectionName, [_where], sortBy, topLimit)
    ) as Observable<QueryDocumentSnapshot<T>[]>
  }

  public idMapFromSnap<T extends HasId>(snap: DocumentSnapshot<T>): T {
    const item = snap.data()!
    item.id = snap.id
    return item
  }

  /***
 
  export declare function doc(
    firestore: Firestore, path: string, ...pathSegments: string[]):
    DocumentReference<DocumentData, DocumentData>;

  export declare function doc<AppModelType, DbModelType extends DocumentData>(
    reference: CollectionReference<AppModelType, DbModelType>, path?: string, ...pathSegments: string[]):
    DocumentReference<AppModelType, DbModelType>;

 */

  public getItem<T>(collectionName: string, ident: string) {
    return docData(this.ref(collectionName, ident), {
      idField: "id"
    }) as Observable<T & { id: string }>
  }

  public getSnap<T>(
    collectionName: string,
    ident: string,
    callback: (doc: DocumentSnapshot<T>) => void
  ) {
    return onSnapshot(
      this.ref(collectionName, ident) as DocumentReference<T>,
      callback
    )
  }

  public getItemSnapshot<T>(collectionName: string, ident: string) {
    return getDoc(this.ref(collectionName, ident)) as Promise<
      DocumentSnapshot<T>
    >
  }

  public changes(collectionName: string, ident: string) {
    return sortedChanges(
      this._getQuery(collectionName, [[this.documentId, "==", ident]])
    )
  }

  public addItem<T>(collectionName: string, item: T) {
    return addDoc(this.col(collectionName), item as { [key: string]: unknown })
  }

  public updateItem(
    collectionName: string,
    ident: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    item: any,
    setUpdatedAt = true
  ) {
    if (setUpdatedAt) {
      item.updatedAt = this.timestamp()
    }
    return updateDoc(this.ref(collectionName, ident), item)
  }

  public incrementItem(
    collectionName: string,
    ident: string,
    field: string,
    amount: number
  ) {
    return updateDoc(this.ref(collectionName, ident), {
      [field]: increment(amount)
    })
  }

  public deleteItem(collectionName: string, ident: string) {
    return deleteDoc(this.ref(collectionName, ident))
  }

  public getIn<T>(
    collectionName: string,
    field: string | FieldPath,
    values: unknown[]
  ) {
    values = [...values]
    if (values.length) {
      const observables: Observable<T[]>[] = []
      while (values.length) {
        const _values = values.splice(0, 30)
        observables.push(
          collectionData(
            this._getQuery(collectionName, [[field, "in", _values]]),
            { idField: "id" }
          ).pipe(map((items) => items as T[]))
        )
      }
      return combineLatest(observables).pipe(map((lists) => lists.flat(1)))
    }
    return of([])
  }
}
